<template>
  <!-- EN-1517: a reload never opens the panel by itself. When it is closed
       and there are cards, only their counts show; a click opens the panel. -->
  <q-btn
    v-if="!panelVisible && sessionCount > 0"
    dense
    no-caps
    unelevated
    size="sm"
    icon="cloud_download"
    color="white"
    text-color="dark"
    class="download-session-counts"
    :label="countsLabel"
    :aria-label="`Download Sessions: ${countsLabel}`"
    @click="openPanel"
  >
    <q-tooltip>Show Download Sessions</q-tooltip>
  </q-btn>
  <div v-if="panelVisible && sessionCount > 0" class="download-session-panel">
    <div class="panel-header">
      <q-icon name="cloud_download" size="sm" class="q-mr-xs" />
      <span class="panel-title">Download Sessions</span>
      <q-space />
      <!-- EN-1517: retry is admin-only (same gate as queueing). -->
      <q-btn
        v-if="showsRetryButton(timedOutCount, { canManageDownloads })"
        flat
        dense
        no-caps
        size="sm"
        icon="replay"
        :aria-label="retryLabel"
        class="q-mr-sm retry-timed-out-btn"
        :loading="retrying"
        :disable="retrying"
        @click="retryTimedOut"
      >
        <!-- Full label on wide screens, just the count on phones
             (src/css/download-session-panel-layout.css). -->
        <span class="retry-label-full q-ml-xs">{{ retryLabel }}</span>
        <span class="retry-label-short q-ml-xs">({{ timedOutCount }})</span>
        <q-tooltip>
          Request every timed-out download again with the same settings
        </q-tooltip>
      </q-btn>
      <q-btn flat dense round icon="close" size="sm" @click="closePanel">
        <q-tooltip>Hide (the downloads keep running)</q-tooltip>
      </q-btn>
    </div>

    <div class="sessions-list q-px-sm q-pt-xs">
      <div
        v-for="s in allSessions"
        :key="s.downloadSessionId"
        class="session-card q-mb-xs q-pa-xs"
        :class="{
          'session-card--selected':
            sessionCount > 1 && s.downloadSessionId === selectedSessionId,
          'session-card--unknown': s.unknown,
        }"
      >
        <div class="row items-center q-mb-xs">
          <q-icon
            :name="sessionStateIcon(s)"
            :color="sessionStateColor(s)"
            size="xs"
            class="q-mr-xs"
          />
          <span
            class="text-caption text-weight-medium"
            :class="`text-${sessionStateColor(s)}`"
          >
            {{ sessionStateLabel(s) }} &mdash; UID {{ s.uid }}
          </span>
          <q-space />
          <!-- EN-1517: Cancel is admin-only (same gate as queueing). -->
          <q-btn
            v-if="showsCancelButton(s, { canManageDownloads })"
            flat
            dense
            no-caps
            size="xs"
            icon="stop_circle"
            label="Cancel"
            class="text-grey-8 cancel-download-btn"
            :loading="!!s.cancelling"
            :disable="!!s.cancelling"
            @click="cancelSession(s)"
          >
            <q-tooltip>Cancel this download</q-tooltip>
          </q-btn>
          <q-btn
            v-if="s.unknown"
            flat
            dense
            round
            icon="close"
            size="xs"
            class="text-grey-7 dismiss-unknown-btn"
            @click="dismissSession(s)"
          >
            <q-tooltip>Dismiss</q-tooltip>
          </q-btn>
          <q-btn
            flat
            dense
            round
            icon="history"
            size="xs"
            class="text-grey-7"
            @click="openDeviceHistoryForSession(s)"
          >
            <q-tooltip>Device History</q-tooltip>
          </q-btn>
        </div>

        <template v-if="s.downloadSessionId < 0">
          <div
            v-if="s.unknown"
            class="text-caption text-grey-6 q-mb-xs unknown-message"
          >
            <q-icon name="help_outline" size="12px" class="q-mr-xs" />
            {{ unknownMessage }}
          </div>
          <div
            v-else-if="s.state === 'Q'"
            class="text-caption text-grey-6 q-mb-xs row items-center"
          >
            <q-spinner color="grey-6" size="14px" class="q-mr-xs" />
            {{ queuedMessage }}
          </div>
          <div
            v-else-if="s.state === 'A'"
            class="text-caption text-orange q-mb-xs"
          >
            {{ cancelledMessage(s) }}
          </div>
          <div v-else class="text-caption text-grey-6 q-mb-xs row items-center">
            <q-spinner color="grey-6" size="14px" class="q-mr-xs" />
            Waiting for the device to respond
          </div>
          <div
            v-if="s.startDate"
            class="text-caption text-grey-6"
            style="font-size: 10px"
          >
            {{ formatDate(s.startDate) }} &mdash; {{ formatDate(s.endDate) }}
          </div>
        </template>
        <template v-else>
          <q-linear-progress
            :value="s.percentComplete / 100"
            :color="sessionProgressColor(s)"
            :stripe="s.state === 'T'"
            :animation-speed="s.state === 'T' ? 300 : 0"
            rounded
            size="18px"
            class="q-mb-xs progress-bar"
            :class="{ 'progress-bar--active': s.state === 'T' }"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="dark"
                :label="`${s.percentComplete}%`"
                :class="{ 'badge-pulse': s.state === 'T' }"
              />
            </div>
          </q-linear-progress>
          <div class="text-caption text-grey-6" style="font-size: 10px">
            {{ formatDate(s.startDate) }} &mdash; {{ formatDate(s.endDate) }}
            <span v-if="s.passageCount > 0" class="q-ml-xs text-grey-8">
              ({{ s.passageCount }} passages)
            </span>
          </div>
          <div
            v-if="
              s.latestPassageTimestamp && (s.state === 'T' || s.state === 'D')
            "
            class="text-caption text-light-blue"
            style="font-size: 10px"
          >
            <q-icon name="schedule" size="12px" class="q-mr-xs" />
            Latest downloaded timestamp:
            {{ formatDate(s.latestPassageTimestamp) }}
          </div>
          <div v-if="s.state === 'X'" class="text-caption text-red">
            Timed out &mdash; device may be offline.
          </div>
          <div v-else-if="s.state === 'A'" class="text-caption text-orange">
            {{ cancelledMessage(s) }}
          </div>
          <div
            v-if="s.state === 'X' && s.retryError"
            class="text-caption text-red-8 retry-error"
          >
            <q-icon name="error_outline" size="12px" class="q-mr-xs" />
            {{ s.retryError }}
          </div>
          <div
            v-if="isCompletedWithNoNewPassages(s)"
            class="text-caption text-grey-7"
          >
            <q-icon name="info" size="12px" class="q-mr-xs" />
            {{ noNewPassagesMessage }}
          </div>
        </template>
        <div v-if="s.cancelError" class="text-caption text-red-8 cancel-error">
          <q-icon name="error_outline" size="12px" class="q-mr-xs" />
          {{ s.cancelError }}
        </div>
        <div
          v-if="s.message"
          class="text-caption text-grey-7 q-mt-xs session-message"
        >
          <q-icon name="info" size="12px" class="q-mr-xs" />
          {{ s.message }}
        </div>
      </div>
    </div>

    <div v-if="allPassages.length > 0" class="passage-section q-px-sm q-pb-sm">
      <div class="row items-center q-mb-xs">
        <span class="text-caption text-weight-medium text-grey-7">
          Passages ({{ allPassages.length }})
        </span>
      </div>
      <q-table
        v-if="allPassages.length > 0"
        :rows="allPassages"
        :columns="passageColumns"
        row-key="passageId"
        dense
        flat
        :rows-per-page-options="[5, 10, 25]"
        class="passage-table"
      >
        <template #body-cell-uid="props">
          <q-td :props="props">{{ props.row.uid }}</q-td>
        </template>
        <template #body-cell-graph-data="props">
          <q-td :props="props">
            <span v-if="(props.row.passageWaveforms || []).length === 1">
              <q-btn
                v-for="wf in props.row.passageWaveforms"
                :key="wf.passageWaveformId"
                size="sm"
                class="q-mr-xs btn-brand"
                dense
                icon="show_chart"
                :label="getWaveformLabel(wf.passageWaveformType)"
                @click="showChart(props, wf)"
              />
            </span>
            <span v-else-if="(props.row.passageWaveforms || []).length > 1">
              <q-btn-dropdown
                size="sm"
                class="q-mr-xs btn-brand"
                dense
                icon="show_chart"
              >
                <q-list dense>
                  <q-item
                    v-for="wf in props.row.passageWaveforms"
                    :key="wf.passageWaveformId"
                    v-close-popup
                    clickable
                    @click="showChart(props, wf)"
                  >
                    <q-item-section>
                      <q-item-label>{{
                        getWaveformLabel(wf.passageWaveformType)
                      }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </span>
          </q-td>
        </template>
        <template #body-cell-machine-learning-data="props">
          <q-td :props="props">
            <template
              v-for="wf in props.row.passageWaveforms || []"
              :key="wf.passageWaveformId"
            >
              <MLBadge
                :type="wf.passageWaveformType"
                :prediction="wf.mlPrediction"
                :confidence="wf.mlConfidence"
                :results="wf.machineLearningResults"
              />
            </template>
          </q-td>
        </template>
      </q-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DeviceService from '@/services/DeviceService';
import { DownloadSessionCard } from '@/models/DownloadSessionCard';
import { WaveformTypeLabel } from '@/models/PassageWaveform';
import MLBadge from '@/components/MLBadge.vue';
import { EventBus } from '@/utils/EventBus';
import {
  NO_NEW_PASSAGES_MESSAGE,
  QUEUED_MESSAGE,
  UNKNOWN_MESSAGE,
  cancelledMessage,
  isCompletedWithNoNewPassages,
  sessionStateColor,
  sessionStateIcon,
  sessionStateLabel,
} from '@/utils/DownloadSessionDisplay';
import { canManageDownloads } from '@/utils/DownloadSessionAccess';
import { showsCancelButton } from '@/utils/DownloadSessionCancel';
import {
  countsLabel,
  formatServerDate,
  serverDateUtcDay,
} from '@/utils/DownloadSessionSync';
import {
  checkNewRetryableSessions,
  createTimedOutRetrier,
  retryButtonLabel,
  showsRetryButton,
  timedOutSessionIds,
  uncheckedTimedOutSessionIds,
} from '@/utils/DownloadSessionRetry';
import '@/css/download-session-panel-layout.css';

type TimedOutRetrier = ReturnType<typeof createTimedOutRetrier>;

// How long to wait before asking again about sessions whose retryable check
// failed (EN-1506).
const RETRYABLE_RECHECK_DELAY_MS = 15000;

// EN-1517: how often to look for placeholders that waited too long.
const PENDING_EXPIRY_CHECK_MS = 30000;

export default defineComponent({
  name: 'DownloadSessionPanel',
  components: { MLBadge },

  data() {
    return {
      unsubscribe: null as (() => void) | null,
      noNewPassagesMessage: NO_NEW_PASSAGES_MESSAGE,
      queuedMessage: QUEUED_MESSAGE,
      unknownMessage: UNKNOWN_MESSAGE,
      pendingExpiryTimer: null as ReturnType<typeof setInterval> | null,
      stopTokenWatch: null as (() => void) | null,
      retrying: false,
      retrier: null as TimedOutRetrier | null,
      // Ids already sent to /download-sessions/retryable (not re-sent).
      retryableCheckedIds: new Set<number>(),
      retryableRecheckTimer: null as ReturnType<typeof setTimeout> | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
      passageColumns: [
        {
          name: 'uid',
          label: 'UID',
          field: 'uid',
          sortable: true,
        },
        {
          name: 'passageTimeStamp',
          label: 'Timestamp',
          field: 'passageTimeStamp',
          sortable: true,
        },
        {
          name: 'detectionCount',
          label: 'Count',
          field: 'detectionCount',
          sortable: false,
        },
        {
          name: 'detectionFrequency',
          label: 'Frequency',
          field: 'detectionFrequency',
          sortable: false,
          format: (val: number) => (val === 0 ? 'MAG' : val + ' Hz'),
        },
        {
          name: 'graph-data',
          label: 'Data',
          field: 'passageId',
        },
        {
          name: 'machine-learning-data',
          label: 'ML',
          field: 'passageId',
          align: 'center',
        },
        {
          name: 'latitude',
          label: 'Lat',
          field: 'latitude',
          sortable: false,
          format: (val: number) =>
            val != null ? Math.round(val * 1e6) / 1e6 : '',
        },
        {
          name: 'longitude',
          label: 'Lon',
          field: 'longitude',
          sortable: false,
          format: (val: number) =>
            val != null ? Math.round(val * 1e6) / 1e6 : '',
        },
      ] as any[],
    };
  },

  computed: {
    panelVisible(): boolean {
      return this.$store.state.downloadSession.panelVisible;
    },
    sessionCount(): number {
      return Object.keys(this.$store.state.downloadSession.sessions).length;
    },
    allSessions(): DownloadSessionCard[] {
      return this.$store.getters.activeDownloadSessionList;
    },
    selectedSessionId(): number | null {
      return this.$store.state.downloadSession.selectedSessionId;
    },
    // EN-1517: cancel and retry are admin-only, with the same helper as the
    // EN-1516 Download Manager button (hasAnyRole admin/superadmin).
    canManageDownloads(): boolean {
      return canManageDownloads(this.$store.state.user);
    },
    countsLabel(): string {
      return countsLabel(this.$store.getters.downloadSessionCounts);
    },
    timedOutCount(): number {
      return timedOutSessionIds(this.allSessions).length;
    },
    retryLabel(): string {
      return retryButtonLabel(this.timedOutCount);
    },
    // Changes when a timed-out session appears that has not been checked.
    uncheckedTimedOutKey(): string {
      return uncheckedTimedOutSessionIds(this.allSessions).join(',');
    },
    allPassages(): any[] {
      const seen = new Set<number>();
      const combined: any[] = [];
      for (const s of this.allSessions) {
        for (const p of s.passages || []) {
          if (!seen.has(p.passageId)) {
            seen.add(p.passageId);
            combined.push(p);
          }
        }
      }
      combined.sort(
        (a, b) =>
          new Date(b.passageTimeStamp).getTime() -
          new Date(a.passageTimeStamp).getTime()
      );
      return combined;
    },
  },

  watch: {
    uncheckedTimedOutKey: {
      handler() {
        this.checkRetryable();
      },
      immediate: true,
    },
  },

  created() {
    this.retrier = createTimedOutRetrier({
      getSessions: () => this.allSessions,
      retry: async (ids) =>
        (await DeviceService.retryDownloadSessions(ids)).data,
      setRetryError: (sessionId, message) =>
        this.$store.commit('SET_DOWNLOAD_SESSION_RETRY_ERROR', {
          sessionId,
          message,
        }),
      setRetryable: (sessionId, retryable) =>
        this.$store.commit('SET_DOWNLOAD_SESSION_RETRYABLE', {
          sessionId,
          retryable,
        }),
      createPending: (p) =>
        this.$store.dispatch('createPendingDownloadSession', p),
      notify: (message) => this.$store.dispatch('notify', message),
      setBusy: (busy) => {
        this.retrying = busy;
      },
      logError: (message, error) => console.error(message, error),
    });
    // Subscribe to the signal-only action dispatched by Hubs.ts.
    // This avoids deep-watching store state (performance rule #1).
    this.unsubscribe = this.$store.subscribeAction((action: any) => {
      if (action.type === 'downloadSessionStateReceived') {
        this.onDownloadSessionUpdate(action.payload);
      }
      if (action.type === 'mlResultReceived') {
        this.onMlResultReceived(action.payload);
      }
    });
  },

  mounted() {
    EventBus.on('switch-passage', this.handleSwitchPassage as any);
    // EN-1517: rebuild the cards after a page reload (queued, running and
    // the last 24 h). The panel shows itself only if something is queued or
    // running.
    // A mount before sign-in finishes loads once the token arrives.
    if (this.$store.getters.token) {
      this.$store.dispatch('loadRecentDownloadSessions');
    } else {
      this.stopTokenWatch = this.$watch(
        () => this.$store.getters.token,
        (token: string) => {
          if (!token) return;
          this.stopTokenWatch?.();
          this.stopTokenWatch = null;
          this.$store.dispatch('loadRecentDownloadSessions');
        }
      );
    }
    this.pendingExpiryTimer = setInterval(
      () => this.$store.dispatch('expirePendingDownloadSessions'),
      PENDING_EXPIRY_CHECK_MS
    );
  },

  beforeUnmount() {
    this.stopTokenWatch?.();
    this.stopTokenWatch = null;
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.clearTimers();
    if (this.retryableRecheckTimer) {
      clearTimeout(this.retryableRecheckTimer);
      this.retryableRecheckTimer = null;
    }
    EventBus.off('switch-passage', this.handleSwitchPassage as any);
    if (this.pendingExpiryTimer) {
      clearInterval(this.pendingExpiryTimer);
      this.pendingExpiryTimer = null;
    }
  },

  methods: {
    isCompletedWithNoNewPassages,
    cancelledMessage,
    sessionStateIcon,
    sessionStateColor,
    sessionStateLabel,
    showsCancelButton,
    showsRetryButton,

    // EN-1517: the store checks the permission again and handles a 403.
    cancelSession(s: DownloadSessionCard) {
      if (!this.canManageDownloads) return;
      return this.$store.dispatch('cancelDownloadSession', s.downloadSessionId);
    },

    dismissSession(s: DownloadSessionCard) {
      this.$store.dispatch('removeDownloadSession', s.downloadSessionId);
    },

    sessionProgressColor(s: DownloadSessionCard): string {
      if (s.state === 'C') return 'green';
      if (s.state === 'X') return 'red';
      if (s.state === 'A') return 'orange';
      return 'light-blue';
    },

    // EN-1517: hide, don't wipe: the cards stay (and keep updating) so the
    // panel can be reopened, e.g. from the Download Manager.
    closePanel() {
      this.clearTimers();
      this.$store.commit('SET_DOWNLOAD_SESSION_PANEL_VISIBLE', false);
    },

    // EN-1517: the counts button; the cards are already loaded.
    openPanel() {
      this.$store.dispatch('openDownloadSessions');
    },

    onDownloadSessionUpdate(data: any) {
      this.scheduleFetch(data.downloadSessionId);
    },

    onMlResultReceived(data: any) {
      for (const s of this.allSessions) {
        if (s.downloadSessionId < 0) continue;
        if (data?.uids?.includes(s.uid)) {
          this.scheduleFetch(s.downloadSessionId);
        }
      }
    },

    scheduleFetch(sessionId: number) {
      // Trailing debounce: reset on every call
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = setTimeout(() => {
        this.fetchPassagesForSession(sessionId);
        this.clearTimers();
      }, 1500);

      // Max-wait: only set once per burst
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(() => {
          if (this.refreshDebounceTimer) {
            clearTimeout(this.refreshDebounceTimer);
          }
          this.fetchPassagesForSession(sessionId);
          this.clearTimers();
        }, 8000);
      }
    },

    clearTimers() {
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
    },

    fetchPassagesForSession(sessionId: number) {
      const session = this.$store.state.downloadSession.sessions[sessionId] as
        | DownloadSessionCard
        | undefined;
      if (!session) return;
      // Remember the state the list was requested in, so the empty-session
      // message only shows for a list fetched after the session completed.
      const loadedState = session.state;

      DeviceService.getDownloadSessionPassages(sessionId)
        .then((response) => {
          const data = response.data;
          const passages = (data.passageEventList || []).map((p: any) => ({
            passageId: p.passageId,
            uid: p.uid,
            passageTimeStamp: p.passageTimeStamp,
            detectionCount: p.detectionCount,
            detectionFrequency: p.detectionFrequency,
            latitude: p.latitude,
            longitude: p.longitude,
            passageWaveforms: p.passageWaveforms || [],
          }));
          this.$store.commit('SET_DOWNLOAD_SESSION_PASSAGES', {
            sessionId,
            passages,
            loadedState,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch passages for download session', error);
          this.$store.dispatch('notify', error);
        });
    },

    getWaveformLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },

    getMLMean(prediction: boolean, confidence: number): number {
      return (2 * Number(prediction) - 1) * confidence;
    },

    getMLColor(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      if (mean > 0.89) return 'green';
      if (mean > 0.75) return 'green-4';
      if (mean > -0.25) return 'orange';
      if (mean > -0.5) return 'red-3';
      return 'red';
    },

    getMLIcon(color: string): string {
      if (color.includes('red')) return 'block';
      if (color.includes('green')) return 'check_circle_outline';
      return 'help';
    },

    getMLConfidenceLabel(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      return Math.round(50 * mean + 50) + '%';
    },

    showChart(props: any, wf: any) {
      this.$store.dispatch('setPassageDialogSource', 'historic_data');
      const passageCopy = { ...props.row };
      passageCopy.requestedWaveformId = wf.passageWaveformId;
      this.$store.dispatch('addPassageIdsToUpdate', [wf.passageWaveformId]);
      this.$store.dispatch('passageChartShow', passageCopy);
    },

    handleSwitchPassage({
      passage,
      direction,
      type,
    }: {
      passage: any;
      direction: 'next' | 'previous';
      type: string;
    }) {
      const passages = this.allPassages;
      if (!passages || passages.length === 0) return;

      const currentWaveformId =
        passage.requestedWaveformId || passage.passageId;

      const currentIndex = passages.findIndex((psg: any) =>
        (psg.passageWaveforms || []).some(
          (wf: any) => wf.passageWaveformId === currentWaveformId
        )
      );

      if (currentIndex === -1) return;

      const inc = direction === 'next' ? 1 : -1;
      const nextIndex =
        (currentIndex + inc + passages.length) % passages.length;
      const nextRow = passages[nextIndex];
      const nextWf =
        (nextRow.passageWaveforms || []).find(
          (wf: any) => wf.passageWaveformType === type
        ) || (nextRow.passageWaveforms || [])[0];
      if (nextWf) {
        this.showChart({ row: nextRow }, nextWf);
      }
    },

    // EN-1506: ask the WebApi which new timed-out sessions a retry can
    // restart, so the button counts only those (not Iridium, DBLAR, etc.).
    // Ids are marked checked while the request runs and un-marked if it
    // fails, and a failed check is tried again once after a short wait, so a
    // failed request does not hide Retry until the page is reloaded.
    async checkRetryable() {
      // EN-1517: retry is admin-only; others never see the button.
      if (!this.canManageDownloads) return;
      const ids = uncheckedTimedOutSessionIds(this.allSessions);
      if (ids.length === 0) return;
      const { retryable, failedIds } = await checkNewRetryableSessions(
        ids,
        this.retryableCheckedIds,
        async (chunk) =>
          (
            await DeviceService.checkDownloadSessionsRetryable(chunk)
          ).data
      );
      retryable.forEach((value, sessionId) =>
        this.$store.commit('SET_DOWNLOAD_SESSION_RETRYABLE', {
          sessionId,
          retryable: value,
        })
      );
      if (failedIds.length > 0) {
        console.error('Could not check which downloads can be retried', {
          failedIds,
        });
        if (!this.retryableRecheckTimer) {
          this.retryableRecheckTimer = setTimeout(() => {
            this.retryableRecheckTimer = null;
            this.checkRetryable();
          }, RETRYABLE_RECHECK_DELAY_MS);
        }
      }
    },

    // EN-1506: retry the retryable timed-out sessions (chunks of 100). The
    // server re-queues each original download (same UID, type and range) and
    // returns a result per session. A card is replaced by a new pending card
    // only when its retry was accepted; the others keep a short error. The
    // guard, dispatch and fixed error message live in createTimedOutRetrier.
    retryTimedOut() {
      return this.retrier?.run();
    },

    openDeviceHistoryForSession(s: DownloadSessionCard) {
      this.$store.dispatch('selectDevice', { uid: s.uid });
      this.$store.dispatch('historicDataShow', {
        fromDate: serverDateUtcDay(s.startDate),
        toDate: serverDateUtcDay(s.endDate),
      });
    },

    formatDate(dateStr: string): string {
      return formatServerDate(dateStr);
    },
  },
});
</script>

<style scoped>
/* The panel box, its header and the phone layout (EN-1506) live in
   src/css/download-session-panel-layout.css, shared with
   e2e/download-session-panel-mobile.unit.spec.ts. */

/* EN-1517: the counts button shown while the panel is closed. */
.download-session-counts {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.22);
}

.sessions-list {
  max-height: 200px;
  overflow-y: auto;
}

.session-card {
  background: #f8f9fb;
  border-radius: 6px;
  border: 1px solid #e0e3ea;
}

.passage-section {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #e0e3ea;
}

.retry-error {
  font-size: 11px;
}

.session-card--selected {
  border-color: #4fc3f7;
}

.session-card--unknown {
  background: #f1f1f1;
  opacity: 0.85;
}

.cancel-error,
.unknown-message {
  font-size: 11px;
}

.session-message {
  font-size: 11px;
}

.passage-table {
  max-height: 200px;
  font-size: 11px;
}

.passage-table :deep(.q-table__top),
.passage-table :deep(.q-table__bottom) {
  padding: 4px 8px;
}

.passage-table :deep(th),
.passage-table :deep(td) {
  padding: 2px 6px;
  font-size: 11px;
}

/* ── Progress bar shine + pulse ── */
.progress-bar {
  position: relative;
  overflow: hidden;
}

.progress-bar--active :deep(.q-linear-progress__track) {
  overflow: hidden;
}

.progress-bar--active::after {
  content: '';
  position: absolute;
  top: 0;
  left: -50%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.35),
    transparent
  );
  animation: shimmer 2s ease-in-out infinite;
  pointer-events: none;
  border-radius: inherit;
}

@keyframes shimmer {
  0% {
    left: -50%;
  }
  100% {
    left: 150%;
  }
}

.badge-pulse {
  animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.75;
    transform: scale(1.08);
  }
}
</style>
